import { useMemo, useState } from 'react'
import {
  type OptionalArtifact,
  selectUnavailable,
  stalenessOf,
  useDataHealthStore,
} from '../data/health'
import { useMeta } from '../data/hooks'
import { formatDate } from '../detail/format'
import { useLocale } from '../detail/useLocale'
import { useStrings } from '../i18n'

/**
 * What is wrong with the data, said once for the whole page.
 *
 * Three conditions can be true while the app itself is working, and a visitor
 * can act on all three - reload, come back later, or discount a number they can
 * see. None of them is a reason to blank the map, so none of them renders as an
 * error page:
 *
 * - **stale** - `meta.generated` is older than a missed daily run;
 * - **degraded** - the publish declares itself as built without Open Charge Map,
 *   so every voltage on the page is derived rather than declared;
 * - **partial** - `status.json` or `prices.json` did not load, so live
 *   availability or tariffs are missing while everything else is fine.
 *
 * A failure of `meta.json` itself is deliberately silent here. It carries no
 * charger data, and a visitor cannot do anything about it; the about page, which
 * is where those numbers are actually read, says so in place.
 */

type NoticeId = 'stale' | 'degraded' | 'partial'

const ARTIFACT_LABEL_KEYS = {
  status: 'liveStatus',
  prices: 'prices',
} as const satisfies Record<OptionalArtifact, 'liveStatus' | 'prices'>

/** One day, expressed in the hours the age is measured in. */
const HOURS_PER_DAY = 24

export const DataHealthBanner = () => {
  const strings = useStrings()
  const locale = useLocale()
  const { resource } = useMeta()
  const unavailable = useDataHealthStore(selectUnavailable)
  const [dismissed, setDismissed] = useState<readonly NoticeId[]>([])

  const meta = resource.status === 'ready' ? resource.value : null

  const staleness = useMemo(() => (meta === null ? null : stalenessOf(meta)), [meta])

  const notices: { id: NoticeId; title: string; body: string; tone: 'warn' | 'info' }[] = []

  if (staleness?.stale === true) {
    const age =
      staleness.ageHours >= HOURS_PER_DAY
        ? strings.dataHealth.ageDays(Math.floor(staleness.ageHours / HOURS_PER_DAY))
        : strings.dataHealth.ageHours(staleness.ageHours)

    notices.push({
      id: 'stale',
      tone: 'warn',
      title: strings.dataHealth.staleTitle,
      body: strings.dataHealth.staleBody(age, formatDate(staleness.generated, locale)),
    })
  }

  if (meta?.degraded === true)
    notices.push({
      id: 'degraded',
      tone: 'info',
      title: strings.dataHealth.degradedTitle,
      body: strings.dataHealth.degradedBody,
    })

  if (unavailable.length > 0)
    notices.push({
      id: 'partial',
      tone: 'warn',
      title: strings.dataHealth.partialTitle,
      body: strings.dataHealth.partialBody(
        unavailable.map((artifact) => strings.dataHealth[ARTIFACT_LABEL_KEYS[artifact]]).join(', '),
      ),
    })

  const showing = notices.filter((notice) => !dismissed.includes(notice.id))
  if (showing.length === 0) return null

  return (
    // The live region is the text, not the row: with `role="status"` on the
    // wrapper a screen reader also announces the dismiss button every time a
    // notice appears.
    <div className="ev-notices">
      {showing.map((notice) => (
        <div key={notice.id} className="ev-notice" data-tone={notice.tone}>
          <span className="ev-notice__mark" aria-hidden="true" />
          <p className="ev-notice__text" role="status">
            <strong className="ev-notice__title">{notice.title}</strong> {notice.body}
          </p>
          <button
            type="button"
            className="ev-notice__dismiss"
            onClick={() => setDismissed((current) => [...current, notice.id])}
            aria-label={strings.app.dismiss}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      ))}
    </div>
  )
}
